import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { crmTerritorialAPI } from '../../services/api';
import { Loader2, ArrowUpDown, History } from 'lucide-react';

const ETAPAS = ['prospecto', 'contacto_inicial', 'calificado', 'visita_programada',
  'visita_realizada', 'negociacion', 'propuesta_enviada', 'documentacion', 'cierre_pendiente',
  'cerrado_ganado', 'cerrado_perdido'];

// P3F — Cambio de etapa (avance o retroceso) con motivo y siguiente acción obligatorios.
// Reutiliza el pipeline y la auditoría existentes; no crea un tablero paralelo.
export const OportunidadEtapa = ({ oportunidad, asesores, onCerrar, onActualizado }) => {
  const { toast } = useToast();
  const [etapa, setEtapa] = useState('');
  const [motivo, setMotivo] = useState('');
  const [accion, setAccion] = useState('');
  const [fecha, setFecha] = useState('');
  const [responsable, setResponsable] = useState(oportunidad.asesor?.id || '');
  const [enviando, setEnviando] = useState(false);
  const [historial, setHistorial] = useState([]);

  useEffect(() => {
    crmTerritorialAPI.historialEtapas(oportunidad.oportunidad_id)
      .then((d) => setHistorial(d.eventos || [])).catch(() => setHistorial([]));
  }, [oportunidad.oportunidad_id]);

  const delTerritorio = asesores.filter((a) => a.seccion_id === oportunidad.seccion_id);
  const indiceActual = ETAPAS.indexOf(oportunidad.etapa);
  const sentido = etapa ? (ETAPAS.indexOf(etapa) > indiceActual ? 'Avance' : 'Retroceso') : null;
  const listo = etapa && etapa !== oportunidad.etapa && motivo.trim().length >= 10
    && accion.trim() && fecha && responsable;

  const guardar = async () => {
    setEnviando(true);
    try {
      const r = await crmTerritorialAPI.cambiarEtapaOportunidad(oportunidad.oportunidad_id, {
        etapa_nueva: etapa,
        motivo: motivo.trim(),
        siguiente_accion: accion.trim(),
        siguiente_accion_fecha: new Date(fecha).toISOString(),
        siguiente_accion_responsable_id: responsable,
      });
      toast({
        title: `Etapa actualizada (${r.sentido})`,
        description: `${r.etapa_anterior} → ${r.etapa_nueva}. Siguiente acción asignada a ${r.siguiente_accion.responsable}.`,
      });
      onActualizado();
      onCerrar();
    } catch (e) {
      toast({ title: 'Cambio rechazado', description: e.message, variant: 'destructive' });
    } finally { setEnviando(false); }
  };

  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent className="max-w-2xl" data-testid="oportunidad-etapa-dialog">
        <DialogHeader>
          <DialogTitle>{oportunidad.nombre}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Etapa actual:</span>
            <Badge className="bg-indigo-100 text-indigo-800"
                   data-testid="oportunidad-etapa-actual">{oportunidad.etapa}</Badge>
            {oportunidad.inactiva && (
              <Badge className="bg-amber-100 text-amber-800"
                     data-testid="oportunidad-etapa-inactiva">
                {oportunidad.dias_sin_actividad} días sin actividad
              </Badge>
            )}
          </div>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center">
              <ArrowUpDown className="h-4 w-4 mr-2 text-gray-400" />
              Avanzar o retroceder etapa {sentido && `· ${sentido}`}
            </h3>
            <select value={etapa} onChange={(e) => setEtapa(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="oportunidad-etapa-select">
              <option value="">Elige la nueva etapa</option>
              {ETAPAS.filter((x) => x !== oportunidad.etapa).map((x) => (
                <option key={x} value={x}>
                  {x} {ETAPAS.indexOf(x) > indiceActual ? '(avance)' : '(retroceso)'}
                </option>
              ))}
            </select>
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                   placeholder="Motivo obligatorio (mínimo 10 caracteres, queda en la auditoría)"
                   data-testid="oportunidad-etapa-motivo" />
            <Input value={accion} onChange={(e) => setAccion(e.target.value)}
                   placeholder="Siguiente acción obligatoria (se crea como tarea pendiente)"
                   data-testid="oportunidad-etapa-accion" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input type="datetime-local" value={fecha} onChange={(e) => setFecha(e.target.value)}
                     data-testid="oportunidad-etapa-fecha" />
              <select value={responsable} onChange={(e) => setResponsable(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm bg-white"
                      data-testid="oportunidad-etapa-responsable">
                <option value="">Responsable de la siguiente acción</option>
                {delTerritorio.map((a) => (
                  <option key={a.asesor_id} value={a.asesor_id}>{a.nombre}</option>
                ))}
              </select>
            </div>
            <Button onClick={guardar} disabled={!listo || enviando}
                    data-testid="oportunidad-etapa-guardar-btn">
              {enviando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar cambio de etapa
            </Button>
            <p className="text-xs text-gray-500">
              Sin motivo, siguiente acción, fecha y responsable de la misma sección no se guarda
              nada. No se envían correos ni mensajes externos.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold flex items-center">
              <History className="h-4 w-4 mr-2 text-gray-400" />
              Historial de etapas ({historial.length})
            </h3>
            <ul className="text-xs space-y-1" data-testid="oportunidad-etapa-historial">
              {historial.length === 0 && <li className="text-gray-500">Sin movimientos.</li>}
              {historial.map((e, i) => (
                <li key={i} className="text-gray-700 border-b last:border-0 py-1">
                  {e.fecha ? new Date(e.fecha).toLocaleString('es-MX') : '—'} · {e.usuario} ·{' '}
                  {e.etapa_anterior || '—'} → {e.etapa_nueva} ({e.sentido}) · {e.motivo}
                  {e.siguiente_accion && ` · próxima: ${e.siguiente_accion}`}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OportunidadEtapa;
